import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowSquareDown,
  faArrowSquareRight,
} from '@whitebox-learning/fontawesome-pro-regular-svg-icons';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import { Loading } from '../Loading/Loading';
import { VERTICAL, HORIZONTAL } from '../../constants/common';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';

import './Quiz.sass';

export default class Quiz extends Component {
  constructor(props) {
    super(props);

    this.state = {
      index: 0,
      hasSentCompletedRequest: false,
    };

    this.nextQuestionButtonRef = React.createRef();
    this.showResultsButtonRef = React.createRef();
  }

  resume() {
    // find the first question that is unanswered
    const index = this.props.questions.reduce((carry, q, idx) => {
      if (carry > -1) {
        return carry;
      }
      const resp = this.props.responses.find((r) => r.questionId === q.id);
      return !resp || resp.isAnswered === false ? idx : carry;
    }, -1);

    this.setState({
      index: Math.max(0, index),
    });
  }

  /**
   * @deprecated - The old react UI requires the user to click "Show Results" to
   * trigger this action.  Once the old UI has been replaced, the backend can be
   * modified to automatically mark an exercise as finished once all questions
   * have been answered.
   */
  autoMarkComplete() {
    if (typeof this.props.score !== 'number' && this.isAllAnswered()) {
      this.setState({
        hasSentCompletedRequest: true,
      });
      this.props.finishExercise(this.props.code);
    }
  }

  componentDidMount() {
    this.resume();

    if (!this.state.hasSentCompletedRequest) {
      this.autoMarkComplete();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.code !== prevProps.code) {
      this.resume();
    }

    if (!this.state.hasSentCompletedRequest) {
      this.autoMarkComplete();
    }
  }

  getStudentAnswer(questionId) {
    return this.props.responses.find((item) => item.questionId === questionId);
  }

  isQuestionCompleted(response) {
    return response && response.isAnswered === true;
  }

  getNumCompleted() {
    return this.props.responses.filter((response) => response.isAnswered)
      .length;
  }

  isAllAnswered() {
    return (
      this.props.questions.length > 0 &&
      this.getNumCompleted() === this.props.questions.length
    );
  }

  nextQuestion = () => {
    if (this.state.index < this.props.questions.length) {
      this.setState(
        {
          index: this.state.index + 1,
        },
        () => {
          // Focus the new question after state update
          const question = this.props.questions[this.state.index];
          if (question) {
            const questionRef = this.refs['question' + question.id];
            if (questionRef && questionRef.focusQuestion) {
              setTimeout(() => {
                questionRef.focusQuestion();
              }, 100);
            }
          }
        }
      );
    }
  };

  handleAnswerCorrect = () => {
    // Focus Next Question or Show Results button when answer is correct
    setTimeout(() => {
      if (this.state.index >= this.props.questions.length - 1) {
        // Last question - focus Show Results button
        if (this.showResultsButtonRef.current) {
          this.showResultsButtonRef.current.focus();
        }
      } else {
        // Focus Next Question button
        if (this.nextQuestionButtonRef.current) {
          this.nextQuestionButtonRef.current.focus();
        }
      }
    }, 100);
  };

  render() {
    if (!this.props.questions.length) {
      return <Loading message="Loading quiz..." />;
    }

    const question = this.props.questions[this.state.index];
    if (!question) {
      return null;
    }

    const response = this.getStudentAnswer(question.id);
    const completed = this.isQuestionCompleted(response);
    let button = null;
    if (completed && this.state.index >= this.props.questions.length - 1) {
      button = (
        <Button
          variant="primary"
          size="sm"
          onClick={this.props.showResults}
          ref={this.showResultsButtonRef}
        >
          Show Results
        </Button>
      );
    } else if (completed) {
      button = (
        <Button
          variant="primary"
          size="sm"
          className="next-question-button"
          disabled={this.state.index >= this.props.questions.length - 1}
          onClick={this.nextQuestion}
          ref={this.nextQuestionButtonRef}
        >
          Next Question
        </Button>
      );
    }
    const position = this.state.index + 1;

    return (
      <div className="activity-container">
        <div className="activity-title">
          <div>{this.props.name}</div>
          <ButtonGroup size="xs">
            <Button
              variant="default"
              title="Dock to Side"
              onClick={() => this.props.setOrientation(VERTICAL)}
              disabled={this.props.orientation === VERTICAL}
            >
              <FontAwesomeIcon icon={faArrowSquareRight} />
            </Button>
            <Button
              variant="default"
              title="Dock to Bottom"
              onClick={() => this.props.setOrientation(HORIZONTAL)}
              disabled={this.props.orientation === HORIZONTAL}
            >
              <FontAwesomeIcon icon={faArrowSquareDown} />
            </Button>
          </ButtonGroup>
        </div>
        <div className="d-flex justify-content-between quiz-subtitle p-2">
          <span>
            Question {position} of {this.props.questions.length}
          </span>
          {button}
        </div>

        <div className="box box-scroll">
          <div className="flex-grow-1 d-flex flex-column justify-content-between bg-white">
            <MultipleChoiceQuestion
              key={question.id}
              {...question}
              ref={'question' + question.id}
              quizId={this.props.code}
              chooseAnswer={this.props.chooseAnswer}
              onAnswerCorrect={this.handleAnswerCorrect}
              checking={response && response.checking === true}
              response={response}
            />
          </div>
        </div>
      </div>
    );
  }
}

Quiz.propTypes = {
  code: PropTypes.string.isRequired,
  name: PropTypes.string,
  questions: PropTypes.array,
  responses: PropTypes.array,
  score: PropTypes.number,
  chooseAnswer: PropTypes.func.isRequired,
  showResults: PropTypes.func.isRequired,
  finishExercise: PropTypes.func.isRequired,
  orientation: PropTypes.oneOf([VERTICAL, HORIZONTAL]),
  setOrientation: PropTypes.func.isRequired,
};

Quiz.defaultProps = {
  code: '',
  name: '',
  questions: [],
  responses: [],
  score: undefined,
  orientation: HORIZONTAL,
};
